/**
 * The live next step under the three planning gates users most often leave on: the credit wall, "first page
 * ready" and "design and architecture ready" (24 Oct 2026). It prices the gate's one step against the live
 * balance and, while the balance cannot start it, names the plan's next credits. Wording: `nextStepCopy`.
 */
import React, { useEffect } from 'react';

import { usePhaseBuildState } from '../../hooks/usePhaseBuildState';
import { eventService } from '../../services/eventService';
import type { CreditRefillStatus } from '../../services/subscriptionService';
import { useAppDispatch } from '../../store/hooks';
import { api } from '../../store/services/api';
import { useGetDesignPagesQuery } from '../../store/services/designApi';
import { useGetPlanningStartQuoteQuery } from '../../store/services/planningStartQuoteApi';
import { getPhaseActionCreditShortfall } from '../../utils/creditGate';
import {
  buildStep, creditWallFacts, creditWallStep, firstPageStep, pagesStillToDesign,
  type NextGrant, type NextStepView, type StepQuote,
} from '../../utils/nextStepCopy';
import { useNextCreditRefill } from '../CreditRefillNotice';

import { usePhaseBuildSelectionPresentation } from './phaseTaskSelectionStore';
import './GateNextStep.css';

export const NEXT_STEP_PHASES = new Set(['insufficient_credits', 'first_page', 'review_build']);

export interface GateNextStepArgs {
  projectId?: string | null;
  entryId: string;
  phase: string;
  /** The stored gate message; the first-page step reads the page name and design time from it. */
  storedContent: string;
  /** The gate's own step button label (Continue, Resume First Page, Generate All Pages …). */
  press: string;
  /** Only the newest gate: older ones are history. */
  active: boolean;
}

const QUOTE_EVENTS = ['credits_update', 'insufficient_credits', 'planning_complete'] as const;
const BILLING_EVENTS = ['billing_topup_success', 'billing_upgrade_success'];

/** A daily grant or a top-up lifts a stale quote at once; so does coming back to the tab. */
const useQuoteRefresh = (projectId: string | null | undefined, active: boolean): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!active || !projectId) return undefined;
    const refresh = () => { dispatch(api.util.invalidateTags([{ type: 'PlanningStartQuote', id: projectId }])); };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    const unsubscribers = QUOTE_EVENTS.map((name) => eventService.subscribe(name, refresh));

    BILLING_EVENTS.forEach((name) => window.addEventListener(name, refresh));
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      BILLING_EVENTS.forEach((name) => window.removeEventListener(name, refresh));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, dispatch, projectId]);
};

/** "Generate All Pages" priced by the same quote the press re-checks on the server. */
const useFirstPageQuote = (args: GateNextStepArgs, enabled: boolean): StepQuote | null => {
  const { currentData } = useGetPlanningStartQuoteQuery({
    projectId: args.projectId ?? '',
    actions: [{ key: 'continue', phase: 'first_page', label: args.press }],
    entryId: args.entryId,
  }, { skip: !enabled || !args.projectId, refetchOnMountOrArgChange: 30 });
  const action = currentData?.actions.continue;

  useQuoteRefresh(args.projectId, enabled);
  if (!currentData || !action || currentData.unlimited) return null;

  return {
    balance: currentData.balance, required: action.required, shortfall: action.shortfall,
    openEnded: action.open_ended, pages: action.pages, allPagesRequired: action.all_pages_required,
  };
};

const grantOf = (status: CreditRefillStatus | null): NextGrant | null => {
  const grant = status?.next_daily_credit_grant;

  return grant ? {
    amount: grant.amount, scheduledAt: grant.scheduled_at, kind: grant.plan_id === 'free' ? 'free' : 'plan',
  } : null;
};

/** The phase build state and the task picker's scope, as Start Building reads them. */
const useBuildFacts = (projectId: string | null | undefined, enabled: boolean) => {
  const phaseBuild = usePhaseBuildState(enabled ? projectId : null);
  const selection = usePhaseBuildSelectionPresentation(projectId, phaseBuild.state, phaseBuild.isLoading);
  const state = enabled ? phaseBuild.state : null;

  // An empty pick is not a scope: until a task is picked, the phase is what Start Building prices.
  const picked = selection.active && Boolean(selection.selectedTaskIds?.length);

  return state ? {
    state,
    selectedTaskIds: picked ? selection.selectedTaskIds : undefined,
    shortfall: getPhaseActionCreditShortfall(state, selection.creditShortfall, picked),
  } : null;
};

const useDesignGateData = (projectId: string | null | undefined, enabled: boolean) => useGetDesignPagesQuery(
  { projectId: projectId ?? '', light: true, includePlanned: true },
  { skip: !enabled || !projectId },
).currentData;

function nextStepView(
  args: GateNextStepArgs,
  design: ReturnType<typeof useDesignGateData>,
  quote: StepQuote | null,
  build: ReturnType<typeof useBuildFacts>,
  grant: NextGrant | null,
): NextStepView | null {
  if (args.phase === 'review_build') return build ? buildStep(build, grant) : null;
  if (args.phase === 'insufficient_credits') {
    const facts = creditWallFacts(design);

    return facts ? creditWallStep(facts, args.press, grant) : null;
  }
  const remaining = pagesStillToDesign(design);

  return remaining?.length && quote
    ? firstPageStep(args.storedContent, remaining, args.press, quote, grant, design?.live_url) : null;
}

/** The next step view for the newest gate of one of the three phases; null elsewhere or while loading. */
export const useGateNextStep = (args: GateNextStepArgs): NextStepView | null => {
  const live = args.active && NEXT_STEP_PHASES.has(args.phase) && Boolean(args.projectId);
  const design = useDesignGateData(args.projectId, live && args.phase !== 'review_build');
  const quote = useFirstPageQuote(args, live && args.phase === 'first_page');
  const build = useBuildFacts(args.projectId, live && args.phase === 'review_build');
  const draft = nextStepView(args, design, quote, build, null);
  const grant = grantOf(useNextCreditRefill(Boolean(draft && draft.shortfall > 0)));

  return draft && grant ? nextStepView(args, design, quote, build, grant) : draft;
};

/** The step's price, the balance and, while short, the next credits: a small receipt under the gate's text. */
export const GateNextStep: React.FC<{ view: NextStepView }> = ({ view }) => (
  <dl className="gate-next-step" aria-label="Next step" data-next-step-primary={view.primary}>
    {view.rows.map((row) => (
      <div key={row.key} className={`gate-next-step__row gate-next-step__row--${row.key}${
        row.short ? ' gate-next-step__row--short' : ''}`}>
        <dt className="gate-next-step__label">{row.label}</dt>
        <dd className="gate-next-step__value">
          {row.href
            ? <a href={row.href} target="_blank" rel="noopener noreferrer">{row.value}</a>
            : row.value}
        </dd>
      </div>
    ))}
  </dl>
);
